import { RegimeModelStatus, RegimeScope } from '@einhaus/project314-types';
import type { RegimeHorizon, RegimeModelVersion, RegimeModelVersionOverrides, RegimeScopeVersionOverrides } from '@/types/appRouter';

/**
 * The three pinnable classifier scopes. The O'Neil market direction framework is deliberately absent:
 * it has no strategy pin in the contract (`RegimeModelVersionOverrides` carries no O'Neil scope), the
 * stale-lineage banner never judges it, and profiling an older training window legitimately resolves a
 * retired backfilled version — so the editor must not offer to pin it.
 */
export const REGIME_PIN_SCOPES = [RegimeScope.market, RegimeScope.entity, RegimeScope.relative] as const;

/**
 * Compile-time exhaustiveness tripwire, mirroring `REGIME_PIN_SCOPE_LABELS`: a new `RegimeHorizon`
 * member fails this record until it is listed here, so a future horizon can never be silently dropped
 * at hydration and then cleared server-side on the next pin edit.
 */
const REGIME_PIN_HORIZON_MEMBERSHIP = {
    '1d': true,
    '240m': true,
    '30m': true,
    '5m': true
} as const satisfies Record<RegimeHorizon, true>;

/**
 * Canonical display and fingerprint order, matching the backend schema's horizon order. Derived from
 * the exhaustively-checked membership record above; the cast is the standard `Object.keys` boundary
 * narrowing over a literal whose keys are proven to be exactly the `RegimeHorizon` members.
 */
export const REGIME_PIN_HORIZONS = Object.keys(REGIME_PIN_HORIZON_MEMBERSHIP) as readonly RegimeHorizon[];

export const REGIME_PIN_SCOPE_LABELS: Record<RegimeScope, string> = {
    [RegimeScope.market]: 'Market',
    [RegimeScope.entity]: 'Entity',
    [RegimeScope.relative]: 'Relative'
};

/**
 * Rebuilds pins in canonical scope/horizon order, dropping empty scopes and returning `null` when no
 * pin remains. The backend schema is strict — it rejects `{}` and rejects a scope with zero horizons —
 * so every pins value handed to the mutation must pass through this normalization.
 */
export function normalizeRegimeModelVersionOverrides(
    pins: RegimeModelVersionOverrides | null | undefined
): RegimeModelVersionOverrides | null {
    if (!pins) return null;

    const normalized: RegimeModelVersionOverrides = {};

    for (const scope of REGIME_PIN_SCOPES) {
        const scopePins = pins[scope];

        if (!scopePins) continue;

        const normalizedScope: RegimeScopeVersionOverrides = {};

        for (const horizon of REGIME_PIN_HORIZONS) {
            const versionId = scopePins[horizon];

            if (versionId !== undefined) normalizedScope[horizon] = versionId;
        }

        if (Object.keys(normalizedScope).length > 0) normalized[scope] = normalizedScope;
    }

    return Object.keys(normalized).length > 0 ? normalized : null;
}

/**
 * Returns the pins with one scope/horizon cell set (`versionId` number) or cleared (`null`), already
 * normalized: clearing the last pin yields `null`, never `{}` or an empty scope object.
 */
export function applyRegimePinSelection(
    pins: RegimeModelVersionOverrides | null,
    scope: RegimeScope,
    horizon: RegimeHorizon,
    versionId: number | null
): RegimeModelVersionOverrides | null {
    const nextScopePins: RegimeScopeVersionOverrides = { ...pins?.[scope] };

    if (versionId === null) {
        delete nextScopePins[horizon];
    } else {
        nextScopePins[horizon] = versionId;
    }

    return normalizeRegimeModelVersionOverrides({ ...pins, [scope]: nextScopePins });
}

/** Order-independent canonical fingerprint, so re-keyed pin objects never read as an edit. */
export function fingerprintRegimePins(pins: RegimeModelVersionOverrides | null): string {
    return REGIME_PIN_SCOPES.map(
        (scope) => `${scope}:${REGIME_PIN_HORIZONS.map((horizon) => pins?.[scope]?.[horizon] ?? '-').join(',')}`
    ).join(';');
}

/**
 * Human-readable one-liners for a pin set, in canonical order — e.g. `Market · 1d → v62`. Used by the
 * read-only pin fallback and by the copy-mode note so carried-over pins are never invisible.
 */
export function listRegimePinDescriptions(pins: RegimeModelVersionOverrides | null): string[] {
    return REGIME_PIN_SCOPES.flatMap((scope) =>
        REGIME_PIN_HORIZONS.flatMap((horizon) => {
            const versionId = pins?.[scope]?.[horizon];

            return versionId === undefined ? [] : [`${REGIME_PIN_SCOPE_LABELS[scope]} · ${horizon} → v${versionId}`];
        })
    );
}

export interface RegimePinChange {
    scope: RegimeScope;
    horizon: RegimeHorizon;
    /** `null` means the cell follows the active model instead of pinning a version. */
    fromVersionId: number | null;
    toVersionId: number | null;
    /** Save-bar and confirmation-dialog line, e.g. `Regime pin market · 1d: active → v62`. */
    label: string;
}

const describeRegimePinCell = (versionId: number | null): string => (versionId === null ? 'active' : `v${versionId}`);

/** One entry per scope/horizon cell whose pin differs between the persisted baseline and the staged form. */
export function buildRegimePinChanges(
    baselinePins: RegimeModelVersionOverrides | null,
    stagedPins: RegimeModelVersionOverrides | null
): RegimePinChange[] {
    const changes: RegimePinChange[] = [];

    for (const scope of REGIME_PIN_SCOPES) {
        for (const horizon of REGIME_PIN_HORIZONS) {
            const fromVersionId = baselinePins?.[scope]?.[horizon] ?? null;
            const toVersionId = stagedPins?.[scope]?.[horizon] ?? null;

            if (fromVersionId === toVersionId) continue;

            changes.push({
                fromVersionId,
                horizon,
                label: `Regime pin ${scope} · ${horizon}: ${describeRegimePinCell(fromVersionId)} → ${describeRegimePinCell(toVersionId)}`,
                scope,
                toVersionId
            });
        }
    }

    return changes;
}

export const hasRegimePinChanges = (
    baselinePins: RegimeModelVersionOverrides | null,
    stagedPins: RegimeModelVersionOverrides | null
): boolean => fingerprintRegimePins(baselinePins) !== fingerprintRegimePins(stagedPins);

export interface ActiveRegimeModelResolution {
    activeVersionId: number | null;
    /**
     * More than one active model covers this scope and horizon. The backend treats that as a hard
     * failure, so the editor surfaces the contradiction instead of silently picking one.
     */
    hasActiveConflict: boolean;
}

export function resolveActiveRegimeModelVersion(
    regimeModelVersions: readonly RegimeModelVersion[],
    scope: RegimeScope,
    horizon: RegimeHorizon
): ActiveRegimeModelResolution {
    const activeVersions = regimeModelVersions.filter(
        (version) => version.scope === scope && version.status === RegimeModelStatus.active && version.horizons.includes(horizon)
    );

    if (activeVersions.length > 1) return { activeVersionId: null, hasActiveConflict: true };

    return { activeVersionId: activeVersions[0]?.id ?? null, hasActiveConflict: false };
}

export interface RegimePinVersionOption {
    versionId: number;
    label: string;
}

export type RegimePinState =
    /** No pin — the cell resolves whatever model is active when a run starts. */
    | 'unpinned'
    /** Pinned to the version that is also currently active: resolution is frozen but not diverging today. */
    | 'pinnedToActive'
    /** Pinned to a version other than the current active (or there is no active): resolution diverges from actives. */
    | 'pinnedOverride';

export interface RegimePinHorizonCell {
    horizon: RegimeHorizon;
    pinnedVersionId: number | null;
    activeVersionId: number | null;
    hasActiveConflict: boolean;
    /** The pinned version id is not in the model catalog for this scope and horizon; rendered honestly, never dropped. */
    isPinnedVersionUnknown: boolean;
    pinState: RegimePinState;
    options: RegimePinVersionOption[];
}

export interface RegimePinScopeRow {
    scope: RegimeScope;
    label: string;
    cells: RegimePinHorizonCell[];
}

const describeRegimeModelVersionOption = (version: RegimeModelVersion): string => {
    const statusSuffix = version.status === RegimeModelStatus.active ? '' : ` (${version.status})`;

    return `v${version.id} · ${version.key}${statusSuffix}`;
};

/**
 * One row per pinnable scope with one cell per horizon: the current pin, the current active model, the
 * selectable version options (newest first), and the derived pin state. A pinned version missing from
 * the catalog still renders as a synthetic option so the select shows the strategy's real state.
 */
export function buildRegimePinEditorRows(params: {
    pins: RegimeModelVersionOverrides | null;
    regimeModelVersions: readonly RegimeModelVersion[];
}): RegimePinScopeRow[] {
    return REGIME_PIN_SCOPES.map((scope) => ({
        cells: REGIME_PIN_HORIZONS.map((horizon) => {
            const pinnedVersionId = params.pins?.[scope]?.[horizon] ?? null;
            const { activeVersionId, hasActiveConflict } = resolveActiveRegimeModelVersion(params.regimeModelVersions, scope, horizon);

            const selectableVersions = params.regimeModelVersions
                .filter((version) => version.scope === scope && version.horizons.includes(horizon))
                .sort((first, second) => second.id - first.id);

            const options: RegimePinVersionOption[] = selectableVersions.map((version) => ({
                label: describeRegimeModelVersionOption(version),
                versionId: version.id
            }));

            const isPinnedVersionUnknown =
                pinnedVersionId !== null && !selectableVersions.some((version) => version.id === pinnedVersionId);

            if (isPinnedVersionUnknown && pinnedVersionId !== null) {
                options.unshift({ label: `v${pinnedVersionId} · unknown version`, versionId: pinnedVersionId });
            }

            const pinState: RegimePinState =
                pinnedVersionId === null ? 'unpinned' : pinnedVersionId === activeVersionId ? 'pinnedToActive' : 'pinnedOverride';

            return {
                activeVersionId,
                hasActiveConflict,
                horizon,
                isPinnedVersionUnknown,
                options,
                pinState,
                pinnedVersionId
            };
        }),
        label: REGIME_PIN_SCOPE_LABELS[scope],
        scope
    }));
}

export type StrategySaveGate = 'regimePinConfirmation' | 'attachmentConfirmation' | 'commit';

/**
 * Decides what a save attempt must do next. Regime pin changes gate first — changing which classifier
 * versions a strategy resolves wedges comparability with everything profiled under the old resolution,
 * so it requires its own informed confirmation before the (also-guarded) live attachment change. The
 * mutation fires only once every staged guarded change has been explicitly confirmed; cancelling any
 * dialog therefore applies nothing.
 */
export function resolveStrategySaveGate(params: {
    hasStagedRegimePinChange: boolean;
    hasStagedAttachmentChange: boolean;
    regimePinChangeConfirmed: boolean;
    attachmentChangeConfirmed: boolean;
}): StrategySaveGate {
    if (params.hasStagedRegimePinChange && !params.regimePinChangeConfirmed) return 'regimePinConfirmation';
    if (params.hasStagedAttachmentChange && !params.attachmentChangeConfirmed) return 'attachmentConfirmation';

    return 'commit';
}
